/**
 * One-time registration of the MapLibre web-worker URL
 * (REQ-UI-002; ADR-016 §4; ADR-027 §1).
 *
 * # Why
 *
 * MapLibre GL renders on a worker pool: tile parsing, geometry
 * tessellation and symbol layout all run off the main thread, and the
 * :class:`maplibre-gl.Map` constructor acquires those workers before it
 * paints anything. If the worker script cannot be fetched, the map never
 * initializes — the canvas mounts and stays empty, with no thrown error
 * on the main thread.
 *
 * MapLibre **6** ships that worker as a *separate* module file
 * (``maplibre-gl-worker.mjs``, which itself imports
 * ``maplibre-gl-shared.mjs``). MapLibre 4 inlined it, which is why this
 * module did not need to exist before. When no worker URL is configured,
 * MapLibre 6 derives one relative to its own module URL:
 *
 * .. code-block:: js
 *
 *    const name = url.endsWith("-dev.mjs")
 *      ? "maplibre-gl-worker-dev.mjs" : "maplibre-gl-worker.mjs";
 *    return new URL(`./${name}`, url).href;
 *
 * In a Vite production build that derivation resolves to
 * ``/assets/maplibre-gl-worker.mjs`` — a file Vite never emits, because
 * nothing in the module graph imports it. The result is a 404 the app
 * does not surface and a permanently blank map. This was the whole
 * failure mode of the 4.7.1 → 6.5.0 bump (#961): ``npm run build``
 * succeeds, every unit test passes (they drive a stubbed ``Map``), and
 * the deployed nginx bundle serves a map that cannot start.
 *
 * # How
 *
 * Importing the worker through Vite's ``?worker&url`` suffix puts it in
 * the module graph, so Vite bundles it **with its shared chunk inlined**
 * and emits a content-hashed asset, handing back that asset's URL. We
 * pass that URL to :func:`maplibre-gl.setWorkerUrl` before any
 * :class:`Map` is constructed. The worker is then an ordinary hashed
 * build artifact: same-origin, cache-busting, and carried in the nginx
 * image by the existing ``dist/`` copy (ADR-027 §1) with no Dockerfile
 * or nginx change.
 *
 * ``worker.format: "es"`` in :file:`vite.config.ts` is load-bearing —
 * MapLibre constructs the worker with ``{ type: "module" }`` for any URL
 * that is not ``.cjs``, so the emitted bundle must itself be an ES
 * module.
 *
 * # Why a dedicated leaf module
 *
 * Registration is a process-global MapLibre side effect, exactly like
 * the ``pmtiles://`` protocol registration next door. Housing it in a
 * leaf module with an idempotence guard (mirroring
 * :func:`registerPmtilesProtocol` in :file:`pmtiles-protocol.ts`, and
 * ``registerGlyphIcons`` in :file:`glyphs.ts`) keeps the call site in
 * :func:`useMap`'s mount effect declarative — every mount calls it,
 * exactly one registration happens — and keeps the worker asset out of
 * modules that tests import constantly.
 *
 * Cross-references:
 *   - REQ-UI-002 (WebGL map surface); ADR-016 §4 (WebGL rendering).
 *   - ADR-027 §1 (UI nginx serves the built bundle same-origin).
 *   - map/use-map.ts (the mount-effect call site).
 *   - map/pmtiles-protocol.ts (the sibling registration this mirrors).
 *   - ui/e2e/map-view.spec.ts (the browser-level guard: a stubbed-``Map``
 *     unit test cannot observe a missing worker asset).
 */
import { setWorkerUrl } from "maplibre-gl";
// Vite emits the MapLibre worker as a content-hashed, self-contained ES
// module and resolves this import to its URL. The ``?worker&url`` suffix
// is what puts the file in the build graph at all — see the module
// docstring for why the default derivation cannot work here.
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

/**
 * Module-level idempotence guard. MapLibre's worker URL is a
 * process-global setting; setting it twice is harmless but pointless,
 * and the guard gives tests a single well-defined registration to
 * assert on.
 */
let configured = false;

/**
 * Point MapLibre at the Vite-bundled worker asset (idempotent).
 *
 * Called from :func:`useMap`'s mount effect **before** the
 * :class:`maplibre-gl.Map` is constructed — the constructor acquires the
 * worker pool immediately, so a late call would leave the first map on
 * the broken default-derivation path.
 */
export function configureMaplibreWorker(): void {
  if (configured) {
    return;
  }
  configured = true;
  setWorkerUrl(maplibreWorkerUrl);
}
